import React from "react";
import { useNavigate } from "react-router-dom";
import PageContainer from "@/components/layout/PageContainer";
import {
  ArrowUpRight,
  BarChart3,
  Boxes,
  Gauge,
  ShoppingBag,
  Trophy,
} from "lucide-react";
import { useLocalization } from "@/lib/localization";

export default function Analytics() {
  const navigate = useNavigate();
  const { messages } = useLocalization();
  const copy = messages.analytics;
  const insightCards = [
    {
      ...copy.cards.restock,
      path: "/analytics/restock-plan",
      icon: Boxes,
      tone: "text-warning bg-warning/10 ring-warning/15",
    },
    {
      ...copy.cards.buyingPatterns,
      path: "/analytics/customer-buying-patterns",
      icon: ShoppingBag,
      tone: "text-primary bg-primary/10 ring-primary/15",
    },
    {
      ...copy.cards.turnover,
      path: "/analytics/turnover",
      icon: Gauge,
      tone: "text-success bg-success/10 ring-success/15",
    },
    {
      ...copy.cards.demandForecast,
      path: "/analytics/demand-forecast",
      icon: BarChart3,
      tone: "text-primary bg-primary/10 ring-primary/15",
    },
    {
      ...copy.cards.topPerformers,
      path: "/analytics/top-performers",
      icon: Trophy,
      tone: "text-warning bg-warning/10 ring-warning/15",
    },
  ];

  return (
    <PageContainer className="px-4 py-6 sm:px-6 lg:px-8">
      <section className="mx-auto w-full max-w-6xl">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{copy.analyticsTitle}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {copy.analyticsSubtitle(insightCards.length)}
            </p>
          </div>
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          {insightCards.map((card) => {
            const Icon = card.icon;
            const isClickable = Boolean(card.path);
            const CardTag = isClickable ? "button" : "article";

            return (
              <CardTag
                key={card.title}
                onClick={isClickable ? () => navigate(card.path) : undefined}
                className="group luxe-card luxe-card-hover rounded-lg p-4 text-left sm:p-5"
                type={isClickable ? "button" : undefined}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md ring-1 ${card.tone}`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                          {card.metric}
                        </p>
                        <h3 className="mt-1 text-[17px] font-semibold leading-6 text-foreground">
                          {card.title}
                        </h3>
                      </div>
                      <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
                    </div>

                    <p className="mt-3 text-sm leading-6 text-muted-foreground">
                      {card.detail}
                    </p>

                    <div className="mt-4 rounded-md border border-border/70 bg-background/60 px-3 py-2.5">
                      <p className="text-sm leading-5 text-foreground/85">
                        {card.suggestion}
                      </p>
                    </div>
                  </div>
                </div>
              </CardTag>
            );
          })}
        </div>
      </section>
    </PageContainer>
  );
}
